import { AssetCategoryModel, AssetGroupCategoryModel, AssetInformation, MasterAsset } from "../store/models/asset.model";
import { CostCenterModel } from "src/app/store/models/asset.model";

const DEFAULT_IMAGE_PATH = "asset-icons/placeholder/icon.png";

const ASSET_PATH_CANDIDATES = (localUrl: string): string[] => {
  if (!localUrl) return [];
  if (localUrl.startsWith("assets/")) {
    const relative = localUrl.slice("assets/".length);
    return [
      localUrl,
      `../assets/${relative}`,
      `../../assets/${relative}`,
      `../../../assets/${relative}`,
    ];
  }
  return [
    `assets/${localUrl}`,
    `../assets/${localUrl}`,
    `../../assets/${localUrl}`,
    `../../../assets/${localUrl}`,
  ];
};

export const getAssetIcon = (
  dirPath: string,
  localUrl: string,
  dynamicUrl: string,
): string => {
  if (localUrl) {
    if (localUrl.startsWith("assets/")) {
      return localUrl;
    }
    return `${dirPath}/${localUrl}`;
  }
  return dynamicUrl || `${dirPath}/${DEFAULT_IMAGE_PATH}`;
};

export const onImageError = (
  event: Event,
  localUrl: string,
  dynamicUrl?: string,
) => {
  const img = event.target as HTMLImageElement;
  // Strip origin so comparisons work with both relative and absolute src values.
  const currentSrc = img.src.replace(window.location.origin + "/", "");

  const candidates = [
    ...ASSET_PATH_CANDIDATES(localUrl),
    ...(dynamicUrl ? [dynamicUrl] : []),
    DEFAULT_IMAGE_PATH,
  ];

  const next = candidates.find(
    (candidate) => candidate && currentSrc !== candidate && !img.src.endsWith(candidate),
  );
  img.src = next ?? DEFAULT_IMAGE_PATH;
};

export const AssetGroupCategories: AssetGroupCategoryModel[] = [
  // {
  //   name: "Loading Unloading Systems",
  //   order: 1,
  // },
  {
    name: "Loading  Unloading Systems",
    order: 1,
  },
  {
    name: "Storage Systems",
    order: 2,
  },
  {
    name: "Conveying Systems",
    order: 3,
  },
  {
    name: "Process Equipment",
    order: 4,
  },
  {
    name: "Buildings",
    order: 5,
  },
  {
    name: "Others",
    order: 6,
  },
  {
    name: "Access and walkways",
    order: 7,
  },
  {
    name: "Other Equipment",
    order: 8,
  },
];

export const matchesDepartment = (
  asset: MasterAsset,
  dept: string,
): boolean => {
  if (!dept) return false;
  for (const subtype of asset?.assetInformation?.subType ?? []) {
    for (const segment of subtype?.segments ?? []) {
      if (segment === dept) {
        return true;
      }
    }
  }
  return false;
};

export type GroupedMasterAsset = { category: string; assets: MasterAsset[] };

export const sortAssetsInGroups = (
  groups: GroupedMasterAsset[],
): GroupedMasterAsset[] =>
  groups.map((item) => ({
    category: item.category,
    assets: item.assets.sort((a, b) =>
      (a?.assetInformation?.title ?? "")
        .toLowerCase()
        .localeCompare((b?.assetInformation?.title ?? "").toLowerCase()),
    ),
  }));

/**
 * Groups a flat list of master assets into categories.
 * Assets whose department segment does not match `department` are placed in
 * "Others" (unless `skipMatchingDepartment` is true).
 * The resulting groups are sorted by category order, with "Others" last.
 */
export const groupMasterAssets = (
  masterAssets: MasterAsset[],
  department: string,
  skipMatchingDepartment: boolean,
): GroupedMasterAsset[] => {
  const grouped = masterAssets.reduce(
    (acc, item) => {
      const shouldBeInOthers =
        !skipMatchingDepartment && !matchesDepartment(item, department);
      const type = shouldBeInOthers
        ? "Others"
        : (item?.assetInformation?.category?.name ?? "Others");
      (acc[type] ||= []).push(item);
      return acc;
    },
    {} as { [key: string]: MasterAsset[] },
  );

  const sorted = Object.entries(grouped)
    .map(([category, assets]) => ({
      category,
      assets,
      order: assets?.[0]?.assetInformation?.category?.order ?? 0,
    }))
    .sort((a, b) => {
      if (a.category === "Others") return 1;
      if (b.category === "Others") return -1;
      return (a?.order ?? 0) - (b?.order ?? 0);
    });

  return sortAssetsInGroups(sorted);
};

/**
 * Applies an icon map onto already-grouped master assets.
 * Matches first by `assetInformation.type`, then by `assetInformation.title`
 * (both normalised: lowercased, `_` and `-` replaced with spaces).
 */
export const applyIconMap = (
  groups: GroupedMasterAsset[],
  iconMap: { [key: string]: string },
): GroupedMasterAsset[] =>
  groups.map((item) => ({
    category: item.category,
    assets: item.assets.map((asset) => {
      const typeKey = asset.assetInformation?.type?.toLowerCase() ?? "";
      const titleKey =
        asset.assetInformation?.title
          ?.toLowerCase()
          .replace(/[_-]/g, " ")
          .trim() ?? "";

      let mappedIcon = iconMap[typeKey];
      if (!mappedIcon) {
        for (const k in iconMap) {
          if (k.replace(/[_-]/g, " ").trim() === titleKey) {
            mappedIcon = iconMap[k];
            break;
          }
        }
      }

      return {
        ...asset,
        assetInformation: {
          ...asset.assetInformation,
          icon: mappedIcon ?? asset.assetInformation?.icon,
        },
      };
    }),
  }));

/**
 * Full pipeline: filters `allMasterAssets` by site categories, then groups and
 * sorts them. Returns both the filtered flat list and the grouped list.
 *
 * @param allMasterAssets  Raw master assets from the API.
 * @param siteCategories   Map of category IDs that belong to the site.
 * @param department       Department segment for the current site.
 * @param siteSegmentsList List of known department segments.
 */
export const processMasterAssets = (
  allMasterAssets: MasterAsset[],
  siteCategories: Map<string, boolean>,
  department: string,
  siteSegmentsList: string[],
): {
  masterAssets: MasterAsset[];
  groupedMasterAssets: GroupedMasterAsset[];
} => {
  const masterAssets = filterAssetsBasedOnModule(
    allMasterAssets,
    siteCategories,
  );
  const skipMatchingDepartment =
    !department || !siteSegmentsList?.includes(department);
  const groupedMasterAssets = groupMasterAssets(
    masterAssets,
    department,
    skipMatchingDepartment,
  );
  return { masterAssets, groupedMasterAssets };
};


export function parseCreatedAt(val: string): number {
  if (!val) return 0;
  const parts = val.split(" ");
  if (parts.length < 2) return 0;
  const [datePart, timePart] = parts;
  const dateSections = datePart.split("-");
  if (dateSections.length !== 3) return 0;
  const [dd, mm, yyyy] = dateSections;
  const ts = new Date(`${yyyy}-${mm}-${dd}T${timePart}`).getTime();
  return isNaN(ts) ? 0 : ts;
}

export function getFirstCharAfterDot(input: string): string | "" {
  if (typeof input !== "string") return "";
  const dotIndex = input.indexOf(".");
  if (dotIndex === -1 || dotIndex === input.length - 1) return "";
  return input.charAt(dotIndex + 1);
}

export function findCostCenterByAssetCode(
  code: string,
  costCenters: CostCenterModel[],
): CostCenterModel {
  if (!code || code === "--") return {} as CostCenterModel;
  const firstChar = getFirstCharAfterDot(code);
  if (!firstChar) return {} as CostCenterModel;
  return costCenters?.find((costCenterItem) => costCenterItem.order === firstChar) ?? {} as CostCenterModel;
}

export const getCurrentBogProModuleMap = (
  bogProModules: AssetCategoryModel[],
  routeName: string,
): Map<string, boolean> => {
  const hasModules = new Map<string, boolean>();
  if (
    routeName.includes("settings/structural") ||
    routeName.includes("settings/site-setup") ||
    routeName.includes("/assessment/asset-assessment")
  ) {
    (
      bogProModules
        ?.filter((module) => module.categoryType === "structural")
        .map((module) => module.id ?? "") ?? []
    ).forEach((module) => hasModules.set(module, true));
  } else {
    (
      bogProModules
        ?.filter((module) => module.categoryType === "materialManagement")
        .map((module) => module.id ?? "") ?? []
    ).forEach((module) => hasModules.set(module, true));
  }
  return hasModules;
};

export const filterAssetsBasedOnModule = (
  assets: MasterAsset[],
  categories: Map<string, boolean>,
): MasterAsset[] => {
  try {
    return assets.filter((asset) => {
      let found = false;
      for (const uniques of asset?.assetInformation?.uniqueSubTypeCategories ??
        []) {
        if (categories.has(uniques?.id ?? "")) {
          found = true;
          break;
        }
      }
      return found;
    });
  } catch (error) {
    return assets;
  }
};

export const AssetFilter = [
  {
    filterSelected: 0,
    filterType: "assetType",
    filterTitle: "Asset Type",
    filters: [
      {
        type: "silo",
        title: "Silo",
        isSelected: false,
      },
      {
        type: "hopper",
        title: "Hopper",
        isSelected: false,
      },
      {
        type: "bridge",
        title: "Bridge",
        isSelected: false,
      },
      {
        type: "bin",
        title: "Bin",
        isSelected: false,
      },
    ],
  },
  {
    filterSelected: 0,
    filterType: "assetArea",
    filterTitle: "Asset Area",
    filters: [
      {
        type: "zone",
        title: "Zone 1",
        isSelected: false,
      },
      {
        type: "zone2",
        title: "Zone 2",
        isSelected: false,
      },
    ],
  },
  {
    filterSelected: 0,
    filterType: "assetStatus",
    filterTitle: "Asset Status",
    filters: [
      {
        type: "assetApprovalPending",
        title: "Asset Approval Pending",
        isSelected: false,
      },
      {
        type: "assetApproved",
        title: "Asset Approved",
        isSelected: false,
      },
      {
        type: "assetInDraft",
        title: "Asset In Draft",
        isSelected: false,
      },
      {
        type: "assetRejected",
        title: "Asset Rejected",
        isSelected: false,
      },
    ],
  },
  {
    filterSelected: 0,
    filterType: "assetSource",
    filterTitle: "Asset Source",
    filters: [
      {
        type: "assetSapSync",
        title: "Sap Sync",
        isSelected: false,
      },
      {
        isSelected: false,
        title: "Bulk Upload",
        type: "assetBulkUpload",
      },
      {
        isSelected: false,
        title: "Manual Creation",
        type: "assetManualCreation",
      },
    ],
  },
];
